import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';

const URL = '/notes/tag-workspace/admin/gold-purities';
const inr = (n) => Math.round(Number(n) || 0).toLocaleString('en-IN');

function Group({ kind, title, rows, picked, onToggle, onAll }) {
  if (!rows.length) return null;
  const all = rows.every((r) => picked.has(r.id));
  return (
    <div className="rounded-xl border border-border" data-testid={`tagws-purity-recalc-${kind}`}>
      <label className="flex items-center gap-2 border-b border-border bg-secondary/50 px-3 py-2 text-xs font-semibold text-foreground">
        <input type="checkbox" checked={all} onChange={() => onAll(rows, !all)} className="h-4 w-4" data-testid={`tagws-purity-recalc-${kind}-all`} /> {title} ({rows.length})
      </label>
      <div className="max-h-56 overflow-y-auto">
        {rows.map((r) => (
          <label key={r.id} className="flex cursor-pointer items-start gap-2 border-b border-border/60 px-3 py-2 text-xs last:border-0 hover:bg-secondary/40">
            <input type="checkbox" checked={picked.has(r.id)} onChange={() => onToggle(r.id)} className="mt-0.5 h-4 w-4" data-testid={`tagws-purity-recalc-pick-${r.id}`} />
            <span className="min-w-0 flex-1">
              <span className="font-mono font-semibold text-foreground">{r.number}</span>{r.ref_no ? <span className="text-muted-foreground"> · {r.ref_no}</span> : null} · {r.customer_name} · Rs {inr(r.total)} · {r.lines} line(s)
              <span className="block text-muted-foreground">{r.purities.join(', ')}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

// After a % change: the super admin picks which past sales / approvals are re-worked with the new %.
export function TagPurityRecalc() {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = () => notesApi.get(`${URL}/affected`).then(({ data: d }) => { setData(d); setPicked(new Set()); }).catch(() => setData({ sales: [], approvals: [] }));
  useEffect(() => { load(); }, []);
  const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const onAll = (rows, on) => setPicked((s) => { const n = new Set(s); rows.forEach((r) => (on ? n.add(r.id) : n.delete(r.id))); return n; });
  const run = async () => {
    setBusy(true);
    try {
      const { data: r } = await notesApi.post(`${URL}/recalc`, { sales: data.sales.filter((x) => picked.has(x.id)).map((x) => x.id), approvals: data.approvals.filter((x) => picked.has(x.id)).map((x) => x.id) });
      toast.success(`${r.updated} list(s) recalculated with the new purity %.`); await load();
    } catch (e) { toast.error(extractError(e, 'Could not recalculate')); } finally { setBusy(false); }
  };
  if (!data) return null;
  const none = !data.sales.length && !data.approvals.length;
  return (
    <div className="space-y-3 rounded-xl border-2 border-amber-500/50 bg-amber-500/5 p-4" data-testid="tagws-purity-recalc">
      <div className="text-sm font-semibold text-foreground">Past lists using a different %</div>
      {none ? <p className="text-xs text-muted-foreground" data-testid="tagws-purity-recalc-none">All sales and approvals already match these %.</p> : (
        <>
          <p className="text-xs text-muted-foreground">Past lists keep the % used when they were made. Tick the ones to re-work with the new %: gold rate, gold value and totals are updated.</p>
          <Group kind="sales" title="Sales" rows={data.sales} picked={picked} onToggle={toggle} onAll={onAll} />
          <Group kind="approvals" title="Approvals" rows={data.approvals} picked={picked} onToggle={toggle} onAll={onAll} />
          <button type="button" onClick={run} disabled={busy || !picked.size} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-amber-600 px-4 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50" data-testid="tagws-purity-recalc-run">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Recalculate selected ({picked.size})
          </button>
        </>
      )}
    </div>
  );
}
